import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    Validators,
} from '@angular/forms';
import { Apollo, gql } from 'apollo-angular';
import { emailValidator } from 'src/app/shared/email-validator/email.validator';
import { View } from '../view.enum';
import { FieldLength } from '../field-length';
import { AddUserInput } from './add-user-input.interface';
import { LoginService } from '../login-service/login.service';
import { LoginState } from '../login-service/login-state.interface';
import { NotificationService } from '../../notification/notification.service';
import { of } from 'rxjs';
import { first } from 'rxjs/operators';

const mutation = gql`
    mutation Mutation($customerID: ID!, $input: AddUserInput!) {
        addUser(customerID: $customerID, input: $input)
    }
`;

@Component({
    selector: 'ols-sign-up',
    templateUrl: './sign-up.component.html',
    styleUrls: ['./sign-up.component.scss'],
})
export class SignUpComponent implements OnInit {
    @Output() viewChange: EventEmitter<View> = new EventEmitter();

    public FieldLength = FieldLength;
    public form: FormGroup;
    public isCreatingAccount = false;

    constructor(
        private fb: FormBuilder,
        private apollo: Apollo,
        private loginService: LoginService,
        private notificationService: NotificationService
    ) {}

    ngOnInit(): void {
        this.form = this.createClientSignUpForm();
    }

    public get clientFirstName(): FormControl {
        return this.form.get('clientFirstName') as FormControl;
    }
    public get clientLastName(): FormControl {
        return this.form.get('clientLastName') as FormControl;
    }
    public get clientMobilePhone(): FormControl {
        return this.form.get('clientMobilePhone') as FormControl;
    }
    public get clientEmailAddress(): FormControl {
        return this.form.get('clientEmailAddress') as FormControl;
    }
    public get clientPassword(): FormControl {
        return this.form.get('clientPassword') as FormControl;
    }
    public get sendEmailNotifications(): FormControl {
        return this.form.get('sendEmailNotifications') as FormControl;
    }
    public get sendTextNotifications(): FormControl {
        return this.form.get('sendTextNotifications') as FormControl;
    }

    private generateMutationInputFromForm(): AddUserInput {
        return <AddUserInput>{
            firstName: this.clientFirstName.value,
            lastName: this.clientLastName.value,
            mobilePhone: this.clientMobilePhone.value,
            email: this.clientEmailAddress.value,
            password: this.clientPassword.value,
            sendEmailNotifications: this.sendEmailNotifications.value,
            sendTextNotifications: this.sendTextNotifications.value,
        };
    }

    public submitForm(): void {
        if (this.form.valid) {
            this.isCreatingAccount = true;

            try {
                const input: AddUserInput = this.generateMutationInputFromForm();
                this.apollo
                    .mutate({
                        mutation: mutation,
                        variables: {
                            input,
                        },
                    })
                    .subscribe(
                        (response: any) => {
                            if (response?.data?.addUser) {
                                this.loginService.logIn(
                                    input.email,
                                    input.password
                                );
                                this.loginService.loginState$
                                    .pipe(first())
                                    .subscribe((updateState: LoginState) => {
                                        if (!updateState) {
                                            //Sign-up succeeded but logging in failed
                                            this.backToLogin();
                                        }
                                    });
                            } else {
                                let errorMessage = null;

                                if (
                                    response.errors?.length === 1 &&
                                    response.errors[0].message?.length
                                ) {
                                    const error = JSON.parse(
                                        response.errors[0].message
                                    );

                                    if (
                                        error.status === 400 &&
                                        error.message?.length
                                    ) {
                                        errorMessage = error.message;
                                    }
                                }

                                this.handleSignUpFailure(errorMessage);
                                return of(null);
                            }
                        },
                        (_err) => {
                            this.handleSignUpFailure();
                            return of(null);
                        }
                    );
            } catch (err) {
                this.handleSignUpFailure();
            }
        }
    }

    private handleSignUpFailure(message?: string) {
        this.isCreatingAccount = false;
        this.notificationService.showFailure(
            message || 'Sign up failed. Please try again.'
        );
    }

    public backToLogin() {
        this.viewChange.emit(View.LOGIN);
    }

    private createClientSignUpForm(): FormGroup {
        return this.fb.group({
            clientFirstName: [
                '',
                [
                    Validators.required,
                    Validators.maxLength(FieldLength.FIRST_NAME_MAX),
                ],
            ],
            clientLastName: [
                '',
                [
                    Validators.required,
                    Validators.maxLength(FieldLength.LAST_NAME_MAX),
                ],
            ],
            clientMobilePhone: [
                '',
                [
                    Validators.required,
                    Validators.maxLength(FieldLength.PHONE_MAX),
                ],
            ],
            clientEmailAddress: [
                '',
                [
                    Validators.required,
                    Validators.maxLength(FieldLength.EMAIL_MAX),
                    emailValidator,
                ],
            ],
            clientPassword: [
                '',
                [
                    Validators.required,
                    Validators.minLength(FieldLength.PASSWORD_MIN),
                ],
            ],
            sendEmailNotifications: [false],
            sendTextNotifications: [false],
        });
    }
}
